<template>
    <Head :title="getTitle($page.component)" />
    <button
        @click="showMobile = !showMobile"
        class="bg-primary-medium fixed left-2 top-2 z-[200] rounded fill-white p-2"
        :aria-expanded="showMobile"
        aria-label="Toggle navigation"
        aria-controls="main-nav"
    >
        <svg
            viewBox="0 0 24 24"
            class="fill-current h-6 w-6 transition-transform duration-300 ease-[cubic-bezier(0.19,1,0.22,1)]"
            :class="{ 'rotate-90': showMobile }"
        >
            <path
                fill-rule="evenodd"
                d="M4 5h16a1 1 0 0 1 0 2H4a1 1 0 1 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2z"
            ></path>
        </svg>
    </button>
    <div
        class="fixed inset-0 z-[99] bg-black/50 transition-opacity duration-300 ease-[cubic-bezier(0.19,1,0.22,1)]"
        :class="
            showMobile
                ? 'pointer-events-auto opacity-100'
                : 'pointer-events-none opacity-0'
        "
        @click="showMobile = false"
    ></div>
    <nav
        id="main-nav"
        aria-label="Main navigation"
        class="bg-primary-medium fixed inset-y-0 left-0 top-0 z-[100] flex h-screen w-48 shrink-0 flex-col overflow-y-auto transition-transform duration-300 ease-[cubic-bezier(0.19,1,0.22,1)]"
        :class="showMobile ? 'translate-x-0' : '-translate-x-full'"
    >
        <ul class="flex flex-col pt-8">
            <li
                v-for="(l, index) in links"
                :key="l.class"
                :data-index="index"
                class="flex flex-col"
            >
                <NavigationLink
                    :l="l"
                    :tabindex="0"
                    @clicked="reorder"
                    :index="index"
                />
                <template v-if="l.class === 'mbbnow'">
                    <BasketballNavigation
                        v-show="isMBBPage"
                        sport="mbb"
                        :conference="$page.props.conference"
                        :team="$page.props.team"
                    />
                </template>
                <template v-if="l.class === 'wbbnow'">
                    <BasketballNavigation
                        v-show="isWBBPage"
                        sport="wbb"
                        :conference="$page.props.conference"
                        :team="$page.props.team"
                    />
                </template>
            </li>
        </ul>
    </nav>
</template>

<script>
import NavigationLink from './NavigationLink.vue';
import BasketballNavigation from './BasketballNavigation.vue';
import { Head } from '@inertiajs/vue3';
import { mapWritableState } from 'pinia';
import { useGeneralStore } from '@/utils/useGeneralStore';
import { router } from '@inertiajs/vue3';
import gsap from 'gsap/all';

export default {
    name: 'NavigationLinks',
    components: {
        Head,
        NavigationLink,
        BasketballNavigation,
    },
    computed: {
        ...mapWritableState(useGeneralStore, ['showMobile']),
        isMBBPage() {
            return [
                'MBBNow',
                'MBB',
                'MBBSchedules',
                'MBBStandings',
                'MBBTop25',
            ].includes(this.$page.component);
        },
        isWBBPage() {
            return ['WBBNow', 'WBB', 'WBBSchedules', 'WBBStandings'].includes(
                this.$page.component,
            );
        },
    },
    data() {
        return {
            links: [
                {
                    icon: 'adam-wood.svg',
                    class: 'default',
                    link: '/',
                    label: 'would wood',
                    component: 'Home',
                    pageTitle: 'Home of',
                },
                {
                    icon: 'admission_tickets.png',
                    class: 'musical-shows',
                    link: '/shows',
                    label: 'shows',
                    component: 'Shows',
                    pageTitle: 'Shows to attend',
                },
                {
                    icon: 'taco.png',
                    class: 'recipes',
                    link: '/recipes',
                    label: 'recipes',
                    component: 'Recipes',
                    pageTitle: 'Recipes to make',
                },
                {
                    icon: 'tv.png',
                    class: 'television',
                    link: route('television'),
                    label: 'television',
                    component: 'Television',
                    pageTitle: 'Upcoming/Watched Television to consume',
                },
                {
                    icon: 'guitar.png',
                    class: 'music',
                    link: route('music'),
                    label: 'music',
                    component: 'Music',
                    pageTitle: 'Music to hear',
                },
                {
                    icon: 'film_projector.png',
                    class: 'movies',
                    link: route('movies'),
                    label: 'movies',
                    component: 'Movies',
                    pageTitle: 'Movies to watch',
                },
                {
                    icon: 'owl.png',
                    class: 'birds',
                    link: route('birds'),
                    label: 'birdings',
                    component: 'Birds',
                    pageTitle: 'Birds to admire',
                },
                {
                    icon: 'camera.png',
                    class: 'photos',
                    link: route('photos'),
                    label: 'photos',
                    component: 'Photos',
                    pageTitle: 'Photos to see',
                },
                {
                    icon: 'basketball.png',
                    class: 'mbbnow',
                    link: `/mbb/america-east/all`,
                    label: 'NCAAM',
                    component: 'MBBNow',
                    pageTitle: "Men's College Basketball Games on Now",
                },
                {
                    icon: 'basketball.png',
                    class: 'wbbnow',
                    link: `/wbb/america-east/all`,
                    label: 'NCAAW',
                    component: 'WBBNow',
                    pageTitle: "Women's College Basketball Games on Now",
                },
                {
                    icon: 'christmas_tree.png',
                    class: 'xmas',
                    link: '/xmas',
                    label: 'XMas cards',
                    component: 'Xmas',
                    pageTitle: 'Christmas Cards',
                },
                {
                    icon: 'email.png',
                    class: 'contact',
                    link: `/contact`,
                    label: 'contact/links',
                    component: 'Contact',
                    pageTitle: 'Contact',
                },
            ],
        };
    },
    mounted() {
        router.on('before', () => {
            this.showMobile = false;
        });
    },
    methods: {
        linkWrapClass() {
            return 'flex flex-wrap group mb-2';
        },
        getTitle(c) {
            let link = this.links.find((l) => l.component === c);
            return link?.pageTitle ? link?.pageTitle : '';
        },
        onEnter(el) {
            gsap.fromTo(
                el,
                { x: -100, opacity: 0 },
                {
                    opacity: 1,
                    x: 0,
                    delay: el.dataset.index * 0.05,
                    duration: 0.2,
                },
            );
        },
        reorder() {},
        reorderArr(i) {
            while (i) {
                [this.links[i], this.links[i - 1]] = [
                    this.links[i - 1],
                    this.links[i],
                ];
                i--;
            }
        },
    },
};
</script>
